import { Capacitor } from "@capacitor/core";
import { useState } from "react";
import { buildInfo } from "../core/buildInfo";
import { featureFlags } from "../core/featureFlags";
import type { ConnectivityState, SyncSummary } from "./model";

interface SystemVersionPanelProps {
  connectivity: ConnectivityState;
  sync: SyncSummary;
}

function connectivityLabel(value: ConnectivityState): string {
  if (value === "online") return "آنلاین";
  if (value === "offline") return "آفلاین";
  return "ارتباط ناپایدار";
}

function formatBuildTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value || "نامشخص";

  return date.toLocaleString("fa-IR-u-ca-persian", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function platformLabel(value: string): string {
  if (value === "android") return "Android";
  if (value === "ios") return "iOS";
  return "Web";
}

export function SystemVersionPanel({ connectivity, sync }: SystemVersionPanelProps) {
  const platform = Capacitor.getPlatform();
  const storage = Capacitor.isNativePlatform() ? "SQLite محلی" : "IndexedDB محلی";
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");

  const diagnostics = [
    `Discus AI V${buildInfo.version}`,
    `clientContractVersion=${buildInfo.clientContractVersion}`,
    `localSchemaVersion=${buildInfo.localSchemaVersion}`,
    `commitSha=${buildInfo.commitSha}`,
    `buildTime=${buildInfo.buildTime}`,
    `platform=${platform}`,
    `storage=${Capacitor.isNativePlatform() ? "sqlite" : "indexeddb"}`,
    `connectivity=${connectivity}`,
    `sync.pending=${sync.pending}`,
    `sync.syncing=${sync.syncing}`,
    `sync.retryable=${sync.retryable}`,
    `sync.conflicts=${sync.conflicts}`,
    `aiUi=${featureFlags.aiUiEnabled ? "enabled" : "disabled"}`,
    "telegramIntegration=disabled",
    "n8nIntegration=disabled",
  ].join("\n");

  async function copyDiagnostics() {
    try {
      await navigator.clipboard.writeText(diagnostics);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  }

  return (
    <section className="section-card" aria-labelledby="system-version-title">
      <div className="section-title-row">
        <div>
          <p className="section-kicker">M4C4</p>
          <h2 id="system-version-title">نسخه و سیستم</h2>
        </div>
        <span className="count-pill">V{buildInfo.version}</span>
      </div>

      <p className="section-note">
        این بخش فقط اطلاعات build و وضعیت محلی برنامه را نمایش می‌دهد و هیچ تنظیم سروری را تغییر
        نمی‌دهد.
      </p>

      <div className="quick-grid">
        <article className="manager-card">
          <p className="section-kicker">نسخه برنامه</p>
          <h3>V{buildInfo.version}</h3>
          <p>قرارداد کلاینت: {buildInfo.clientContractVersion}</p>
          <p>Schema محلی: {buildInfo.localSchemaVersion}</p>
        </article>

        <article className="manager-card">
          <p className="section-kicker">Build</p>
          <h3 className="version-sha">{buildInfo.commitSha}</h3>
          <p>{formatBuildTime(buildInfo.buildTime)}</p>
        </article>

        <article className="manager-card">
          <p className="section-kicker">دستگاه</p>
          <h3>{platformLabel(platform)}</h3>
          <p>ذخیره آفلاین: {storage}</p>
        </article>

        <article className="manager-card">
          <p className="section-kicker">ارتباط</p>
          <h3>{connectivityLabel(connectivity)}</h3>
          <p>
            صف {sync.pending} · ارسال {sync.syncing} · مجدد {sync.retryable} · تعارض{" "}
            {sync.conflicts}
          </p>
        </article>
      </div>

      <div className="manager-card">
        <p className="section-kicker">معماری فعال</p>
        <div className="work-list">
          <div className="work-item done">
            <span className="work-main">
              <strong>AI UI</strong>
              <span>{featureFlags.aiUiEnabled ? "فعال" : "غیرفعال"}</span>
            </span>
          </div>
          <div className="work-item done">
            <span className="work-main">
              <strong>Telegram</strong>
              <span>غیرفعال در V2</span>
            </span>
          </div>
          <div className="work-item done">
            <span className="work-main">
              <strong>n8n</strong>
              <span>غیرفعال در V2</span>
            </span>
          </div>
        </div>
      </div>

      <button type="button" className="secondary-button" onClick={() => void copyDiagnostics()}>
        کپی اطلاعات عیب‌یابی
      </button>

      {copyState === "copied" ? <p className="operation-success">اطلاعات عیب‌یابی کپی شد.</p> : null}
      {copyState === "failed" ? (
        <p className="settings-warning">دسترسی کپی در این محیط در دسترس نیست.</p>
      ) : null}
    </section>
  );
}
